import { useCallback, useEffect, useRef, useState } from 'react';
import invariant from 'tiny-invariant';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { fetchRingValidationV1ControllerGetPictures } from 'generated/apiComponents';
import { DefectPictureGroupDto } from 'generated/apiSchemas';
import { Module_Enum } from 'generated/graphql';

const PAGE_SIZE = 50;

export const useGetRingPictures = (ringId: string) => {
  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site');

  const headers = useRestApiHeaders(Module_Enum.QualityControl);

  const [items, setItems] = useState<DefectPictureGroupDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const nextPage = useRef(0);

  const fetchNextPages = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);

    try {
      let hasMore = true;
      while (hasMore) {
        const response = await fetchRingValidationV1ControllerGetPictures({
          pathParams: {
            constructionSiteId: currentConstructionSiteId,
            ringId,
          },
          queryParams: {
            paginationPage: nextPage.current,
            paginationSize: PAGE_SIZE,
          },
          headers,
        });

        setItems((previousItems) => [...previousItems, ...response.items]);
        nextPage.current += 1;
        hasMore = response.items.length === PAGE_SIZE;
      }
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [currentConstructionSiteId, ringId, headers]);

  useEffect(() => {
    setItems([]);
    nextPage.current = 0;
    fetchNextPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConstructionSiteId, ringId]);

  return { items, isLoading, isError, retry: fetchNextPages };
};
